import type { ResellerCustomer } from "@xop-any-time/shared";
import {
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  type TextFieldProps,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState, type JSX } from "react";
import { useNavigate } from "react-router-dom";

import { keys, listCustomers } from "../../../lib/crmApi.js";

/**
 * New licence — pick WHOSE appliance, then build it.
 *
 * Nothing is created here. The dialog only answers the one question the
 * builder cannot (which customer) and lands in `/licences/new`, where the terms
 * are chosen and Generate creates AND signs in one press. There is no
 * half-generated licence to leave behind if the reseller changes their mind.
 */

export const TRIAL_DAYS = 30;

/** Where a new licence for this customer is built. */
export function newLicencePath(customer: { id: number; name: string }): string {
  return `/licences/new?customer=${customer.id}&name=${encodeURIComponent(customer.name)}`;
}

export function defaultTrialEnd(today = new Date()): string {
  const date = new Date(today);
  date.setHours(0, 0, 0, 0);
  date.setDate(date.getDate() + TRIAL_DAYS);
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
}

interface Props {
  open: boolean;
  onClose: () => void;
}

export function CreateLicenceDialog({ open, onClose }: Props): JSX.Element {
  const navigate = useNavigate();
  const [picked, setPicked] = useState<ResellerCustomer | null>(null);
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (open) setPicked(null);
  }, [open]);

  const customers = useQuery({
    queryKey: keys.customers({ search }),
    queryFn: () => listCustomers({ search }),
    enabled: open,
  });

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>New licence</DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <DialogContentText>
          One licence per appliance. Pick the customer; you choose the term, ports and features next
          and generate the file in one step. A {TRIAL_DAYS}-day trial licence generates at once for a
          customer with no licence yet; a full licence goes to XOP&apos;s executives for approval.
        </DialogContentText>
        <Autocomplete
          options={customers.data?.customers ?? []}
          value={picked}
          loading={customers.isFetching}
          onChange={(_event, next) => {
            setPicked(next);
            // Reopening the list after a pick must offer everyone again, not
            // just the customer whose name now fills the input.
            setSearch("");
          }}
          onInputChange={(_event, next, reason) => {
            // MUI fires "reset" after a selection, carrying the picked LABEL.
            // Searching the server for it would narrow the list to that one
            // customer and leave no way to pick a different one.
            if (reason === "reset") return;
            setSearch(next);
          }}
          getOptionLabel={(option) => option.name}
          isOptionEqualToValue={(a, b) => a.id === b.id}
          filterOptions={(all) => all}
          noOptionsText={search ? "No customer matches." : "Type a company name."}
          renderInput={(params) => (
            // MUI's Autocomplete params carry `className?: string | undefined`
            // on InputLabelProps, which exactOptionalPropertyTypes rejects
            // against TextField's `string` — a typing gap, not a runtime one.
            <TextField {...(params as TextFieldProps)} size="small" label="Customer" autoFocus placeholder="Search companies" />
          )}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!picked}
          onClick={() => {
            if (!picked) return;
            onClose();
            navigate(newLicencePath(picked));
          }}
        >
          Continue
        </Button>
      </DialogActions>
    </Dialog>
  );
}
